import { GlassCard, PillTag } from "@/components/mnf/UI";
import {
  useStoreQuery,
  queries,
  micronutrientPanelKeyFor,
  MICRONUTRIENT_PANELS,
  type MicronutrientPanelKey,
} from "@/lib/store";

/**
 * Life-season micronutrient panel.
 * Shows a curated list of priority nutrients for the sister's current
 * season (prenatal, nursing, or general). A gentle map, not a prescription.
 * Coach Kiley's plan and a sister's provider always override.
 */
export default function NutrientPanelCard({
  userId,
  forceKey,
}: {
  userId: string;
  /** Override season detection (used for previews / docs). */
  forceKey?: MicronutrientPanelKey;
}) {
  const season = useStoreQuery(() => queries.seasonState(userId));
  const panelKey = forceKey ?? micronutrientPanelKeyFor(season);
  const panel = MICRONUTRIENT_PANELS[panelKey];

  const eyebrowLabel =
    panelKey === "prenatal" ? "Nutrient panel · Prenatal season"
      : panelKey === "nursing" ? "Nutrient panel · Nursing season"
      : "Nutrient panel · Daily rhythm";

  return (
    <GlassCard
      padding={20}
      data-testid="card-nutrient-panel"
      style={{
        background: "linear-gradient(135deg, rgba(245,240,232,0.96) 0%, rgba(168,212,184,0.14) 100%)",
        border: "1px solid rgba(30,74,53,0.10)",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-t)", marginBottom: 6 }}
          data-testid="text-nutrient-panel-eyebrow"
        >
          {eyebrowLabel}
        </p>
        <h3
          className="mnf-serif"
          style={{ fontSize: 20, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}
          data-testid="text-nutrient-panel-title"
        >
          {panel.title}
        </h3>
        <p
          className="mnf-body"
          style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 6 }}
          data-testid="text-nutrient-panel-subtitle"
        >
          {panel.subtitle}
        </p>

        {/* Scripture pairing */}
        <div
          style={{
            marginTop: 12,
            padding: "10px 14px",
            borderRadius: 14,
            background: "rgba(160,120,32,0.08)",
            border: "1px solid rgba(160,120,32,0.16)",
          }}
          data-testid="text-nutrient-panel-scripture"
        >
          <p
            className="mnf-serif italic"
            style={{ fontSize: 13.5, color: "var(--mnf-fd)", lineHeight: 1.5 }}
          >
            "{panel.scripture}"
          </p>
          <p
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-gold)", marginTop: 4, fontSize: 10 }}
          >
            {panel.scriptureRef}
          </p>
        </div>
      </div>

      {/* Nutrient rows */}
      <div className="space-y-3">
        {panel.items.map((item) => {
          const id = item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div
              key={item.name}
              data-testid={`row-nutrient-${id}`}
              style={{
                padding: "12px 14px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(30,74,53,0.06)",
              }}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <PillTag tone={item.tone}>{item.targetLabel}</PillTag>
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-serif"
                    style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}
                  >
                    {item.name}
                  </p>
                  <p
                    className="mnf-body"
                    style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 4 }}
                  >
                    {item.why}
                  </p>

                  {/* Sources */}
                  <div className="flex flex-wrap gap-1.5" style={{ marginTop: 8 }}>
                    {item.sources.map((src) => (
                      <span
                        key={src}
                        className="mnf-body"
                        style={{
                          fontSize: 11,
                          color: "var(--mnf-fd)",
                          background: "rgba(30,74,53,0.06)",
                          border: "1px solid rgba(30,74,53,0.08)",
                          borderRadius: 999,
                          padding: "3px 9px",
                          fontWeight: 500,
                        }}
                      >
                        {src}
                      </span>
                    ))}
                  </div>

                  {item.pairing && (
                    <p
                      className="mnf-body italic"
                      style={{ fontSize: 11.5, color: "var(--mnf-t)", lineHeight: 1.5, marginTop: 8 }}
                    >
                      {item.pairing}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <p
        className="mnf-body italic"
        style={{
          fontSize: 11.5,
          color: "var(--mnf-tg)",
          lineHeight: 1.55,
          marginTop: 14,
          textAlign: "center",
        }}
        data-testid="text-nutrient-panel-footer"
      >
        {panel.footer}
      </p>
    </GlassCard>
  );
}
